// src/components/post-card/CommentComposer.tsx
//
// The new design's version of the old CommentBox in its comment / reply form:
// @mentions, attachments (shared rules via useFileQueue) and voice notes.
// Scheduling and polls don't apply to a comment, so they're not offered.

import { useRef, useState } from "react";
import { Avatar, Icon } from "@/lib/ui";
import { useUploadFiles } from "@/hooks/useUploadFiles"; // port this over if it isn't already in the new project
import { getMediaType } from "@/utils/helper"; // same — port if missing
import { useAppSelector } from "@/services/hook";
import type { RootState } from "@/services/store";
import type { MediaItem } from "@/utils/types";
import { useNotify } from "@/hooks/useNotify";
import { useFileQueue } from "@/hooks/useFileQueue";
import { useMentionUsers, useMentions } from "@/hooks/useMentions";
import { MentionDropdown } from "./mention-dropdown";
import { MediaAttachments } from "./media-attachements";
import { PostToolbar } from "./post-toolbar";
import { VoiceRecorderModal } from "./VoiceRecorderModal";
import { useFetchProfile } from "@/hooks/apiHooks";

export interface CommentSubmitPayload {
  message: string;
  mentions: string[];
  mediaFiles: MediaItem[]; // whatever uploadFiles resolves to — not bare URL strings
  mediaType?: string;
}

export function CommentComposer({
  onSubmit,
  isSubmitting,
  placeholder = "Add a comment…",
  autoFocus,
}: {
  onSubmit: (payload: CommentSubmitPayload) => void;
  isSubmitting?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const notify = useNotify();
  const { userObject } = useAppSelector((s: RootState) => s.auth);
  const { data } = useFetchProfile(userObject);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [recorderOpen, setRecorderOpen] = useState(false);
  const { files, addFiles, removeFile, clear, totalMB } = useFileQueue();

  const { mentionableUsers, isLoading: mentionUsersLoading } =
    useMentionUsers();
  const {
    mentionQuery,
    filteredUsers,
    highlightedIndex,
    dropdownPos,
    handleTextareaChange,
    handleKeyDown,
    pickMention,
    closeMentionDropdown,
    mentionIds,
  } = useMentions(textareaRef, mentionableUsers);

  const {
    uploadFiles,
    isUploading,
    reset: resetUpload,
  } = useUploadFiles({
    usid: userObject?.usid,
    onError: (error: any) => notify.error(error?.message || "Upload failed"),
  });

  const canSubmit =
    (text.trim().length > 0 || files.length > 0) &&
    !isSubmitting &&
    !isUploading;

  const submit = async () => {
    if (!canSubmit) return;

    if (files.length > 0) {
      try {
        const mediaLinks = await uploadFiles(files);
        onSubmit({
          message: text.trim(),
          mentions: mentionIds,
          mediaFiles: mediaLinks,
          mediaType: getMediaType(files),
        });
        clear();
        resetUpload();
      } catch (error) {
        console.error("Upload failed:", error);
        return;
      }
    } else {
      onSubmit({ message: text.trim(), mentions: mentionIds, mediaFiles: [] });
    }

    setText("");
  };

  const handleRecordingComplete = (blob: Blob) => {
    const ext = blob.type.includes("mp4")
      ? "m4a"
      : blob.type.includes("ogg")
        ? "ogg"
        : "webm";
    addFiles([
      new File([blob], `voice-note-${Date.now()}.${ext}`, { type: blob.type }),
    ]);
    notify.success("Voice note added successfully");
  };

  return (
    <div className="col gap8" style={{ marginTop: 8 }}>
      <div className="row gap10" style={{ alignItems: "flex-start" }}>
        <Avatar src={data?.data?.profileImageUrl} name="You" size={32} initialsOnly />
        <div className="grow" style={{ position: "relative" }}>
          <textarea
            ref={textareaRef}
            className="input"
            placeholder={placeholder}
            autoFocus={autoFocus}
            rows={1}
            value={text}
            style={{ padding: "9px 13px", resize: "none", width: "100%" }}
            onChange={(e) => {
              setText(e.target.value);
              handleTextareaChange(
                e.target.value,
                e.target.selectionStart ?? 0,
              );
            }}
            onKeyDown={(e) => {
              // Enter submits when the mention dropdown isn't consuming it;
              // useMentions' handleKeyDown calls preventDefault when it does.
              handleKeyDown(e, text, setText);
              if (e.key === "Enter" && !e.shiftKey && mentionQuery === null) {
                e.preventDefault();
                submit();
              }
            }}
          />
          {mentionQuery !== null && (
            <MentionDropdown
              candidates={filteredUsers}
              isLoading={mentionUsersLoading}
              highlightedIndex={highlightedIndex}
              position={dropdownPos}
              onSelect={(user) => pickMention(user, text, setText)}
              onClose={closeMentionDropdown}
            />
          )}
        </div>
        <button
          className="btn btn-blue btn-sm"
          disabled={!canSubmit}
          onClick={submit}
        >
          <Icon n="send" s={15} />
        </button>
      </div>

      <div style={{ paddingLeft: 42 }}>
        <PostToolbar
          onFilesSelected={addFiles}
          ifRecord
          onRecordClick={() => setRecorderOpen(true)}
        />
      </div>

      <div style={{ paddingLeft: 42 }}>
        <MediaAttachments
          files={files}
          onRemove={removeFile}
          totalMB={totalMB}
          maxMB={50}
        />
      </div>

      <VoiceRecorderModal
        isOpen={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onRecordingComplete={handleRecordingComplete}
      />
    </div>
  );
}
